class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

/* CAJ: dynamic collection product images */
(() => {
  const CARD_SELECTOR = '.product-card-wrapper';
  const READY_CLASS = 'caj-gallery-ready';
  const ACTIVE_CLASS = 'caj-gallery-active';
  const MAX_IMAGES = 6;
  const CHANGE_EVERY = 950;
  const MOBILE_SETTLE_DELAY = 220;
  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isCoarsePointer = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;

  if (isReducedMotion) return;

  const productCache = new Map();
  const cardStates = new WeakMap();
  const mobileActive = new Set();
  let mobileSettleTimer;

  const style = document.createElement('style');
  style.textContent = `
    ${CARD_SELECTOR}.${READY_CLASS} .card__media .media--hover-effect > img:not(:first-child) {
      display: none !important;
    }

    ${CARD_SELECTOR}.${READY_CLASS} .card__media .media > img:first-child {
      transition: opacity 180ms ease;
      will-change: opacity;
    }

    ${CARD_SELECTOR}.${ACTIVE_CLASS} .card__media .media > img:first-child {
      opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      ${CARD_SELECTOR}.${READY_CLASS} .card__media .media > img:first-child {
        transition: none;
      }
    }
  `;
  document.head.appendChild(style);

  function getCardState(card) {
    let state = cardStates.get(card);

    if (!state) {
      const image = card.querySelector('.card__media .media > img:first-child');
      state = {
        image,
        images: null,
        index: 0,
        intervalId: null,
        fadeTimer: null,
        intentToken: 0,
        original: image
          ? {
              src: image.getAttribute('src'),
              srcset: image.getAttribute('srcset'),
              sizes: image.getAttribute('sizes'),
              alt: image.getAttribute('alt') || '',
            }
          : null,
      };
      cardStates.set(card, state);
    }

    return state;
  }

  function getProductUrl(card) {
    const link =
      card.querySelector('.card__heading a[href*="/products/"]') ||
      card.querySelector('a.full-unstyled-link[href*="/products/"]') ||
      card.querySelector('a[href*="/products/"]');

    if (!link) return null;

    const url = new URL(link.href, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = url.pathname.replace(/\/$/, '');

    return url;
  }

  function normalizeImageUrl(value) {
    const source =
      typeof value === 'string'
        ? value
        : value && typeof value === 'object'
          ? value.src || value.url
          : null;

    if (!source) return null;

    const url = new URL(source, window.location.origin);
    if (url.hostname.includes('shopify.com') || url.hostname.includes('shopifycdn.com')) {
      url.searchParams.set('width', '900');
    }

    return url.toString();
  }

  function productJsonUrl(productUrl) {
    const url = new URL(productUrl.toString());
    if (!url.pathname.endsWith('.js')) url.pathname += '.js';
    return url.toString();
  }

  async function fetchProductImages(card) {
    const productUrl = getProductUrl(card);
    if (!productUrl) return [];

    const cacheKey = productUrl.pathname;
    if (!productCache.has(cacheKey)) {
      const request = fetch(productJsonUrl(productUrl), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
        .then((product) => {
          const values = Array.isArray(product.images) ? product.images : [];
          return [...new Set(values.map(normalizeImageUrl).filter(Boolean))].slice(0, MAX_IMAGES);
        })
        .catch(() => []);

      productCache.set(cacheKey, request);
    }

    return productCache.get(cacheKey);
  }

  function preloadImages(images) {
    images.slice(1).forEach((src) => {
      const preload = new Image();
      preload.src = src;
    });
  }

  function setImage(card, state, index) {
    if (!state.image || !state.images || state.images.length < 2) return;

    state.index = index % state.images.length;
    const nextSrc = state.images[state.index];

    window.clearTimeout(state.fadeTimer);
    state.image.style.opacity = '0';

    state.fadeTimer = window.setTimeout(() => {
      state.image.removeAttribute('srcset');
      state.image.removeAttribute('sizes');
      state.image.src = nextSrc;
      state.image.style.opacity = '1';
    }, 120);
  }

  function resetCard(card, state) {
    window.clearInterval(state.intervalId);
    window.clearTimeout(state.fadeTimer);
    state.intervalId = null;
    state.index = 0;
    card.classList.remove(ACTIVE_CLASS);

    if (!state.image || !state.original) return;

    state.image.style.opacity = '1';

    if (state.original.src) state.image.setAttribute('src', state.original.src);
    if (state.original.srcset) {
      state.image.setAttribute('srcset', state.original.srcset);
    } else {
      state.image.removeAttribute('srcset');
    }

    if (state.original.sizes) {
      state.image.setAttribute('sizes', state.original.sizes);
    } else {
      state.image.removeAttribute('sizes');
    }

    state.image.setAttribute('alt', state.original.alt);
  }

  function stopCard(card, reset = true) {
    const state = getCardState(card);
    state.intentToken += 1;
    window.clearInterval(state.intervalId);
    window.clearTimeout(state.fadeTimer);
    state.intervalId = null;

    if (reset) resetCard(card, state);
  }

  async function startCard(card) {
    const state = getCardState(card);
    if (!state.image || state.intervalId) return;

    const token = ++state.intentToken;
    const images = state.images || (await fetchProductImages(card));

    if (token !== state.intentToken || !images || images.length < 2) return;

    state.images = images;
    preloadImages(images);
    card.classList.add(READY_CLASS, ACTIVE_CLASS);

    setImage(card, state, 1);
    state.intervalId = window.setInterval(() => {
      setImage(card, state, state.index + 1);
    }, CHANGE_EVERY);
  }

  function closestCard(target) {
    return target instanceof Element ? target.closest(CARD_SELECTOR) : null;
  }

  document.addEventListener(
    'pointerover',
    (event) => {
      if (isCoarsePointer() || event.pointerType !== 'mouse') return;

      const card = closestCard(event.target);
      if (!card || card.contains(event.relatedTarget)) return;
      startCard(card);
    },
    true
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (isCoarsePointer() || event.pointerType !== 'mouse') return;

      const card = closestCard(event.target);
      if (!card || card.contains(event.relatedTarget)) return;
      stopCard(card, true);
    },
    true
  );

  function selectCenteredMobileCards() {
    if (!isCoarsePointer()) return [];

    const viewportCenter = window.innerHeight / 2;
    const maximumDistance = window.innerHeight * 0.34;

    return [...document.querySelectorAll(CARD_SELECTOR)]
      .map((card) => {
        const rect = card.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const visible = rect.bottom > 0 && rect.top < window.innerHeight;
        return { card, distance: Math.abs(center - viewportCenter), visible };
      })
      .filter((item) => item.visible && item.distance <= maximumDistance)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 2)
      .map((item) => item.card);
  }

  function settleMobileCards() {
    if (!isCoarsePointer()) return;

    const selected = new Set(selectCenteredMobileCards());

    mobileActive.forEach((card) => {
      if (!selected.has(card)) {
        stopCard(card, true);
        mobileActive.delete(card);
      }
    });

    selected.forEach((card) => {
      mobileActive.add(card);
      startCard(card);
    });
  }

  function pauseMobileCards() {
    if (!isCoarsePointer()) return;

    mobileActive.forEach((card) => stopCard(card, true));
    mobileActive.clear();

    window.clearTimeout(mobileSettleTimer);
    mobileSettleTimer = window.setTimeout(settleMobileCards, MOBILE_SETTLE_DELAY);
  }

  window.addEventListener('scroll', pauseMobileCards, { passive: true });
  window.addEventListener('resize', pauseMobileCards, { passive: true });
  window.addEventListener('orientationchange', pauseMobileCards, { passive: true });

  document.addEventListener('shopify:section:load', () => {
    window.clearTimeout(mobileSettleTimer);
    mobileSettleTimer = window.setTimeout(settleMobileCards, MOBILE_SETTLE_DELAY);
  });

  const observer = new MutationObserver(() => {
    if (!isCoarsePointer()) return;
    window.clearTimeout(mobileSettleTimer);
    mobileSettleTimer = window.setTimeout(settleMobileCards, MOBILE_SETTLE_DELAY);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', settleMobileCards, { once: true });
  } else {
    settleMobileCards();
  }
})();

